import { useEffect, useState } from "react";

/**
 * Polls /version.json and reports when the build it returns differs from the
 * one baked into the running bundle.
 *
 * The build emits version.json via the versionMetadataPlugin in vite.config.js
 * (release = full git SHA), and netlify.toml serves it with no-store so this
 * fetch always sees the latest deploy. The current bundle's SHA is baked in
 * via process.env.VITE_RELEASE.
 *
 * Returns false in dev (no VITE_RELEASE), on first paint, and while the user
 * is on the latest build. Returns true the first time we see a different
 * release and stays true until the user reloads.
 */

export const VERSION_ENDPOINT = "/version.json";

// 15 minutes — frequent enough to catch a deploy within a coffee break,
// rare enough that an idle tab open all day costs ~96 pings.
export const DEFAULT_POLL_INTERVAL_MS = 15 * 60 * 1000;

// Don't fire the very first check at page load — bootstrap is busy enough
// already and a fresh page is by definition on the freshly-loaded build.
const INITIAL_DELAY_MS = 30 * 1000;

const fetchCurrentRelease = async () => {
    try {
        const res = await fetch(`${VERSION_ENDPOINT}?t=${Date.now()}`, {
            cache: "no-store",
            headers: { Accept: "application/json" },
        });
        if (!res.ok) return null;
        const json = await res.json();
        return typeof json?.release === "string" ? json.release : null;
    } catch (_error) {
        // Network blip / offline / netlify hiccup — try again next tick.
        return null;
    }
};

export const useNewVersionAvailable = ({
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    bakedRelease = process.env.VITE_RELEASE,
} = {}) => {
    const [newVersionAvailable, setNewVersionAvailable] = useState(false);

    useEffect(() => {
        // Skip in dev (no release stamped in) or once we've already detected
        // a new version — the toast remains sticky until reload.
        if (!bakedRelease || newVersionAvailable) return undefined;

        let cancelled = false;

        const check = async () => {
            const remote = await fetchCurrentRelease();
            if (cancelled || !remote) return;
            if (remote !== bakedRelease) {
                setNewVersionAvailable(true);
            }
        };

        const initialTimeout = setTimeout(check, INITIAL_DELAY_MS);
        const interval = setInterval(check, pollIntervalMs);

        // Most stale-tab cases: user comes back to the tab hours later.
        // visibilitychange catches this without waiting for the poll cycle.
        const onVisibility = () => {
            if (document.visibilityState === "visible") void check();
        };
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            cancelled = true;
            clearTimeout(initialTimeout);
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [bakedRelease, pollIntervalMs, newVersionAvailable]);

    return newVersionAvailable;
};
